<template>
  <div
    class="bg-vp-gray-lighter min-h-screen flex flex-col-reverse lg:flex-row gap-4 lg:gap-0 lg:divide-y lg:justify-end w-full divide-x lg:h-screen"
  >
    <template v-if="offerSheet">
      <section class="flex items-center flex-col grow overflow-hidden sm:overflow-auto">
        <TopSummary />
        <div class="w-full h-full relative overflow-hidden flex flex-col">
          <TabGroup>
            <TabList>
              <Tab
                v-for="(label, idx) in tabLabels"
                :key="idx"
                v-slot="{ selected }"
                as="template"
              >
                <button
                  class="px-4 py-2 text-vp-base-900"
                  :class="{ 'font-bold': selected }"
                >
                  {{ label }}
                </button>
              </Tab>
            </TabList>
            <TabPanels class="flex-grow">
              <TabPanel class="h-full">
                <div
                  v-dragscroll
                  class="relative overflow-auto h-full w-full bg-vp-base-800 grab-bing hidden sm:block"
                >
                  <div
                    class="px-8 py-12 mx-auto w-fit h-fit select-none"
                    :style="pdfContainerStyle"
                  >
                    <div v-html="offerSheet.htmlContent" />
                  </div>
                </div>
                <div class="pb-8 md:absolute relative w-full bottom-0 h-16">
                  <div class="flex items-center justify-center absolute h-full w-full">
                    <VPButton
                      label="Download Offer Sheet PDF"
                      theme="primary"
                      size="lg"
                      class="lg:fixed absolute min-w-fit px-5"
                      :disabled="loading"
                      @click.prevent="lockAndDownload"
                    />
                  </div>
                  <div class="btn-group absolute right-10 bottom-2 text-lg text-slate-900 opacity-70 hidden sm:flex">
                    <VPButton
                      :disabled="isOutBtnDisabled"
                      theme="base"
                      size="lg"
                      class="w-10"
                      @click.prevent="zoomPdf('out')"
                    >
                      <MagnifyMinusOutline class="text-2xl" />
                    </VPButton>
                    <VPButton
                      theme="base"
                      size="lg"
                      class="w-12"
                      @click.prevent="resetZoomPdf"
                    >
                      {{ zoomLevel }}%
                    </VPButton>
                    <VPButton
                      :disabled="isInBtnDisabled"
                      theme="base"
                      size="lg"
                      class="w-10"
                      @click.prevent="zoomPdf('in')"
                    >
                      <MagnifyPlusOutline class="text-2xl" />
                    </VPButton>
                  </div>
                </div>
              </TabPanel>
              <TabPanel>
                <div class="px-4">
                  <VPButton
                    class="my-4 px-4"
                    max-width="100%"
                    @click="onCreateVersion"
                  >
                    Create new version
                  </VPButton>
                  <div
                    v-for="version in versions"
                    :key="version.id"
                    class="py-2 flex justify-between items-center border-b hover:bg-vp-base-150"
                    :class="{ 'font-bold': offerSheet.id == version.id }"
                  >
                    <div class="mx-4">
                      {{ version.version }} (<i>{{ version.versionName || 'Unnamed' }}</i>)
                    </div>
                    <VPButton
                      v-show="offerSheet.id != version.id"
                      component="router-link"
                      :to="{ name: 'OfferSheet', params: { offerSheetsId: version.id } }"
                      class="px-4"
                    >
                      Switch to
                    </VPButton>
                  </div>
                </div>
              </TabPanel>
            </TabPanels>
          </TabGroup>
        </div>
      </section>
      <section
        class="offer-sheet-modal-form sheet flex items-center flex-col overflow-y-scroll h-full w-648 flex-shrink-0 overscroll-contain"
      >
        <header class="flex flex-row justify-between items-center w-full p-3">
          <button
            type="button"
            class="text-vp-base text-sm"
            @click.prevent="close"
          >
            Back
          </button>
          <div class="text-vp-primary uppercase font-bold text-sm">
            Offer
          </div>
          <div>
            <OfferSheetMenuDropdown
              @save="saveTemplate"
              @apply="applyTemplate"
            />
          </div>
        </header>
        <main class="w-full p-3">
          <Form
            :form-id="EVENT_NAME_FORM_ID"
            :initial-values="{ name: eventName }"
            :class="{ dirty }"
          >
            <h2>
              <VPInput
                ref="nameRef"
                :disabled="disabled"
                class="vp-event-name"
                input-id="name"
                placeholder="Add event name"
                @blur="eventNameSave"
              />
            </h2>
          </Form>
          <div class="flex py-3 w-full items-center">
            <Profile class="mr-2" />
            <span class="font-bold mr-4 text-sm">Promoter:</span>
            <Form
              :form-id="PROMOTER_NAME_FORM_ID"
              :initial-values="{ promoterName }"
              :class="{ dirty }"
            >
              <span>
                <VPInput
                  :disabled="disabled"
                  class="vp-promoter-name"
                  input-id="promoterName"
                  placeholder="Add promoter name"
                  @blur="promoterNameSave"
                />
              </span>
            </Form>
          </div>
          <div
            v-if="offerSheet.lockedAt"
            class="text-center bg-vp-error my-2 py-1 px-4 rounded-md font-bold text-white"
          >
            The offer sheet has been locked by {{ lockedBy }} at {{ lockedAt }}.
          </div>
          <Offer />
          <Show />
          <Tickets />
          <Expenses />
          <PromoterProfit />
          <Notes />
        </main>
      </section>
    </template>
    <div
      v-else
      class="text-vp-base font-bold text-5xl content-center absolute top-24 left-24"
    >
      Loading...
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Profile from '@/components/icons/Profile.vue';
import Offer from '@/components/offer-sheet/offer/Offer.vue';
import Show from '@/components/offer-sheet/show/Show.vue';
import Tickets from '@/components/offer-sheet/tickets/Tickets.vue';
import Expenses from '@/components/offer-sheet/expenses/Expenses.vue';
import PromoterProfit from '@/components/offer-sheet/promoter-profit/PromoterProfit.vue';
import TopSummary from '@/components/offer-sheet/TopSummary.vue';
import { Form, useFormsStore } from 'vuniform';
import VPInput from '@/components/form/VPInput.vue';
import VPButton from '@/components/form/VPButton.vue';
import OfferSheetMenuDropdown from '@/components/offer-sheet/OfferSheetMenuDropdown.vue';
import Notes from '@/components/offer-sheet/notes/Notes.vue';
import { type SheetVersion, useOfferSheetStore } from '@/stores/offer-sheet';
import { useOfferSheetFormStore, EVENT_NAME_FORM_ID, PROMOTER_NAME_FORM_ID } from '@/stores/offer-sheet-form';
import { MagnifyPlusOutline, MagnifyMinusOutline } from 'mdue';
import { formatDate } from '@/helpers/dateTime';
import { DateTime } from 'luxon';
import { TabGroup, TabList, Tab, TabPanel, TabPanels } from '@headlessui/vue';

const route = useRoute();
const router = useRouter();
const formsStore = useFormsStore();
const offerSheetStore = useOfferSheetStore();
const offerSheetFormStore = useOfferSheetFormStore();
const offerSheet = computed(() => offerSheetStore.offerSheet);
const eventName = computed(() => offerSheetStore.offerSheet?.name || '');
const promoterName = computed(() => offerSheetStore.offerSheet?.promoterName || '');
const loading = computed(() => offerSheetStore.offerSheetLoading);
const templatesLoading = computed(() => offerSheetStore.templateLoading);

const DEFAULT_ZOOM = 100;
const MIN_ZOOM = 50;
const MAX_ZOOM = 200;
const ZOOM_STEP = 10;

const zoomLevel = ref(DEFAULT_ZOOM);

const initOfferSheet = async () => {
  await offerSheetStore.fetchOfferSheet(Number(route.params.offerSheetsId));
  offerSheetStore.loadTemplates();
  offerSheetFormStore.initFromOfferSheet();
};

onMounted(() => {
  initOfferSheet();
});

const saveTemplate = () => {
  let templateName = prompt('Enter template name');
  if (templatesLoading.value || templateName == null || templateName.trim() == '') {
    return;
  }

  templateName = templateName.trim();

  offerSheetStore.createTemplate({
    offerSheetId: offerSheetStore.offerSheet.id,
    templateName: templateName,
  });
};

const applyTemplate = async id => {
  if (confirm('Do you want to apply this template?')) {
    try {
      await offerSheetStore.applyTemplate(id);
      offerSheetStore.offerSheet = null;
      initOfferSheet();
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      // ignored
    }
  }
};

const eventNameSave = () => {
  if (loading.value || !formsStore.isFieldDirty(EVENT_NAME_FORM_ID, 'name')) {
    return;
  }

  offerSheetStore.updateOfferSheet({
    id: offerSheetStore.offerSheet.id,
    name: formsStore.fieldGetValue(EVENT_NAME_FORM_ID, 'name'),
  });

  formsStore.SET_INITIAL_VALUES({
    values: { ...offerSheet.value },
    formId: EVENT_NAME_FORM_ID,
    clearDirty: true,
  });
};

const promoterNameSave = () => {
  if (loading.value || !formsStore.isFieldDirty(PROMOTER_NAME_FORM_ID, 'promoterName')) {
    return;
  }

  offerSheetStore.updateOfferSheet({
    id: offerSheetStore.offerSheet.id,
    promoterName: formsStore.fieldGetValue(PROMOTER_NAME_FORM_ID, 'promoterName'),
  });

  formsStore.SET_INITIAL_VALUES({
    values: { ...offerSheet.value },
    formId: PROMOTER_NAME_FORM_ID,
    clearDirty: true,
  });
};

const lockAndDownload = async () => {
  if (loading.value) {
    return;
  }

  if (offerSheet.value?.pdfDownloadUrl) {
    window.open(offerSheet.value.pdfDownloadUrl, '_blank');
    return;
  }

  try {
    const url = await offerSheetStore.lockSheet({
      offerSheetId: offerSheetStore.offerSheet.id,
    });

    if (!url) {
      alert('Error locking Offer sheet.');
      return;
    }

    window.open(url, '_blank');
  } catch (error) {
    console.error('Error navigating:', error.message);
    alert('Error locking Offer sheet.');
  }
};

const pdfContainerStyle = computed(() => {
  return {
    transform: `scale(${zoomLevel.value}%)`,
    transformOrigin: 'top left',
  };
});

const zoomPdf = (level: 'in' | 'out') => {
  if (level === 'in' && zoomLevel.value < MAX_ZOOM) {
    zoomLevel.value += ZOOM_STEP;
  } else if (level === 'out' && zoomLevel.value > MIN_ZOOM) {
    zoomLevel.value -= ZOOM_STEP;
  }
};

const isOutBtnDisabled = computed(() => {
  if (zoomLevel.value === MIN_ZOOM) {
    return true;
  }
  return false;
});

const isInBtnDisabled = computed(() => {
  if (zoomLevel.value === MAX_ZOOM) {
    return true;
  }
  return false;
});

const resetZoomPdf = () => {
  zoomLevel.value = DEFAULT_ZOOM;
};

const close = () => router.push('/');

const lockedBy = computed(() => offerSheet.value?.lockedBy?.name);
const lockedAt = computed(() => formatDate(offerSheet.value?.lockedAt, DateTime.DATETIME_MED));

const tabLabels = computed(() => {
  return ['Preview', `Versions (${offerSheet.value?.versions?.length || 0})`];
});

const versions = computed(() =>
  (offerSheet.value?.versions || []).reduce((acc, v) => [v, ...acc], [] as SheetVersion[]),
);

watch(
  () => route.params.offerSheetsId,
  (newId, oldId) => {
    if (newId != oldId) {
      initOfferSheet();
    }
  },
);

const onCreateVersion = async () => {
  const versionName = prompt('Enter version name:');
  if (versionName == null || !offerSheet.value) {
    return;
  }

  const { id } = await offerSheetStore.createVersion(offerSheet.value.id, versionName);
  router.push({ params: { offerSheetsId: id } });
};
</script>
